'use client';

import {CatalogOfferProduct, PurchaseReceipt} from '@/types';
import {createContext, PropsWithChildren, useContext, useMemo, useReducer, useState} from 'react';
import {PurchaseInitializer} from './PurchaseInititalizer';
import {PurchaseTrigger} from './PurchaseTrigger';
import {PurchaseConfirmAuth} from './PurchaseConfirmAuth';
import {PurchaseCompletedCleanup} from './PurchaseCompletedCleanup';
import PurchaseCompletedTrigger from './PurchaseCompletedTrigger';

import {
	PurchaseActionType,
	PurchaseActiveActions,
	PurchaseContextAction,
	PurchaseContextProps,
	PurchaseContextState,
	PurchaseEvent,
	PurchaseIdleActions,
	PurchaseReinitializeEvent,
	PurchaseStartEvent,
	PurchaseState,
	PurchaseStepAction
} from './types';

const PurchaseContext = createContext<PurchaseContextProps<PurchaseState> | undefined>(undefined);

export const usePurchaseContext = <T extends PurchaseState>(): PurchaseContextProps<T> => {
	const context = useContext(PurchaseContext);

	if (context === undefined) {
		throw new Error('usePurchaseContext must be used within a PurchaseProvider');
	}

	return context as PurchaseContextProps<T>;
};

const defaultInitialState: PurchaseContextState = {
	currentStepIndex: 0
};

const purchaseStateReducer = (purchaseStates: PurchaseState[]) => {
	return (
		state: PurchaseContextState,
		action: PurchaseStepAction | PurchaseContextAction
	): PurchaseContextState => {
		/**
		 * This internal action is used to switch between purchase contexts, such
		 * as switching from PDP to Cart.
		 * It takes precedence over other actions and will reset the context back
		 * to the inactive state. This handles rare cases where users can
		 * interrupt a purchase flow, mainly when using back/forward navigation
		 * during an active purchase.
		 */
		if (action.type === PurchaseActionType.InternalContextSwitch) {
			return defaultInitialState;
		}

		if (action.dispatchedBy !== state.currentStepIndex) {
			return state;
		}

		switch (action.type) {
			case PurchaseActionType.Start: {
				return {...state, ...action.payload, currentStepIndex: state.currentStepIndex + 1};
			}

			case PurchaseActionType.Next: {
				if (state.currentStepIndex + 1 === purchaseStates.length) {
					return defaultInitialState;
				}

				return {...state, currentStepIndex: state.currentStepIndex + 1};
			}
			case PurchaseActionType.Cancel:
				return defaultInitialState;

			case PurchaseActionType.Reinitialize: {
				const currentStepIndex = (
					action as PurchaseStepAction<PurchaseActionType.Reinitialize>
				).payload?.currentStepIndex;
				if (state.currentStepIndex === 0 && typeof currentStepIndex === 'number') {
					return action.payload as PurchaseContextState;
				}
				return state;
			}

			default:
				return state;
		}
	};
};

interface PurchaseProviderProps extends PropsWithChildren {
	products: Record<'pinnedProducts', CatalogOfferProduct[]>;
}

export const PurchaseProvider = ({children, products}: PurchaseProviderProps) => {
	const [purchaseStates, setPurchaseStates] = useState<PurchaseState[]>([]);
	const [receipt, setReceipt] = useState<PurchaseReceipt | undefined>(undefined);
	const [purchaseState, purchaseStateDispatch] = useReducer(
		purchaseStateReducer(purchaseStates),
		defaultInitialState
	);

	// when a purchase flow is initialized through setPurchaseStates, reset the
	// purchase state to inactive
	const setPurchaseStatesAndReset = (purchaseStates: PurchaseState[]) => {
		setPurchaseStates(purchaseStates);
		purchaseStateDispatch({
			type: PurchaseActionType.InternalContextSwitch
		});
	};

	const actions = useMemo(() => {
		return purchaseStates.reduce<PurchaseContextProps<PurchaseState>['actions']>(
			(acc, state, index) => {
				const cancel: PurchaseEvent = () => {
					purchaseStateDispatch({dispatchedBy: index, type: PurchaseActionType.Cancel});
				};

				const next: PurchaseEvent = () => {
					purchaseStateDispatch({dispatchedBy: index, type: PurchaseActionType.Next});
				};

				const start: PurchaseStartEvent = (payload) => {
					purchaseStateDispatch({
						dispatchedBy: index,
						payload,
						type: PurchaseActionType.Start
					});
				};

				const reinitialize: PurchaseReinitializeEvent = (payload) => {
					purchaseStateDispatch({
						dispatchedBy: index,
						payload,
						type: PurchaseActionType.Reinitialize
					});
				};

				switch (state) {
					case 'inactive': {
						(acc as PurchaseIdleActions)[state] = {
							cancel,
							start,
							reinitialize
						};
						break;
					}
					default: {
						(acc as PurchaseActiveActions)[state] = {
							cancel,
							next
						};
						break;
					}
				}

				return acc;
			},
			{} as PurchaseContextProps<PurchaseState>['actions']
		);
	}, [purchaseStateDispatch, purchaseStates]);

	const currentStep = useMemo<PurchaseContextProps<PurchaseState>>(() => {
		return {
			actions,
			offerId: purchaseState.offerId,
			productId: purchaseState.productId,
			sandboxId: purchaseState.sandboxId,
			offerType: purchaseState.offerType,
			setPurchaseStates: setPurchaseStatesAndReset,
			state: purchaseStates[purchaseState.currentStepIndex],
			receipt,
			setReceipt,
			purchasableProducts: products?.pinnedProducts || []
		};
	}, [
		actions,
		purchaseState,
		purchaseStates,
		setPurchaseStates,
		receipt,
		setReceipt,
		products?.pinnedProducts
	]);

	return (
		<PurchaseContext.Provider value={currentStep}>
			<PurchaseInitializer />
			<PurchaseConfirmAuth />
			<PurchaseTrigger />
			<PurchaseCompletedTrigger />
			<PurchaseCompletedCleanup />
			{children}
		</PurchaseContext.Provider>
	);
};
